import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LoadingSpinner from '../common/LoadingSpinner';
import FilterDropdown from '../common/FilterDropdown';
import {
    ScaleIcon,
    TrophyIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    XMarkIcon,
    CalendarIcon,
    UserCircleIcon
} from '@heroicons/react/24/outline';

const ParentDisciplineTab = ({ 
    childId = '', 
    childrenList = [], 
    initialChildId = '', 
    onChildChange 
}) => {
    const { get, post } = useApi();
    const { showToast } = useAppState();
    const queryClient = useQueryClient();

    // Tab & Search State
    const [activeTab, setActiveTab] = useState('merits'); // 'merits' | 'demerits'
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Filters State
    const [selectedChildId, setSelectedChildId] = useState(childId || initialChildId || '');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    // Appeal Modal State
    const [appealingRecord, setAppealingRecord] = useState(null);
    const [appealText, setAppealText] = useState('');

    // Sync childId from props if provided
    useEffect(() => {
        if (childId) {
            setSelectedChildId(childId);
        } else if (initialChildId && !selectedChildId) {
            setSelectedChildId(initialChildId);
        }
    }, [childId, initialChildId, selectedChildId]);

    // Fallback query for parent profile if childrenList not passed
    const { data: parentMe } = useQuery({
        queryKey: ['parent', 'me'],
        queryFn: () => get('/api/parents/me/'),
        staleTime: 30 * 60 * 1000,
        enabled: childrenList.length === 0,
    });

    const parentChildren = parentMe?.children;
    const children = useMemo(() => {
        return childrenList.length > 0 ? childrenList : (parentChildren || []);
    }, [childrenList, parentChildren]);

    // 1. Fetch Academic Years
    const { data: years = [] } = useQuery({
        queryKey: ['academic-years'],
        queryFn: () => get('/api/academic-years/'),
        staleTime: 60 * 60 * 1000,
    });

    // 2. Fetch Academic Terms
    const { data: terms = [] } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });

    // Auto-select current term/year once on load
    const hasInitializedTerm = useRef(false);
    useEffect(() => {
        const rawTerms = Array.isArray(terms) ? terms : (terms?.results || []);
        if (!hasInitializedTerm.current && rawTerms.length > 0) {
            const current = rawTerms.find(t => t.is_current);
            if (current) {
                setSelectedTerm(String(current.id));
                if (current.year) {
                    setSelectedYear(String(current.year));
                }
            }
            hasInitializedTerm.current = true;
        }
    }, [terms]);

    // 3. Fetch Parent Discipline Records
    const { data: records = [], isLoading } = useQuery({
        queryKey: ['parent-discipline-records', selectedChildId, selectedYear, selectedTerm],
        queryFn: () => {
            const params = new URLSearchParams();
            if (selectedChildId) params.append('child_id', selectedChildId);
            if (selectedYear) params.append('year_id', selectedYear);
            if (selectedTerm) params.append('term_id', selectedTerm);
            const queryStr = params.toString();
            return get(queryStr ? `/api/discipline/parent-records/?${queryStr}` : '/api/discipline/parent-records/');
        },
        staleTime: 30 * 1000,
    });

    // Appeal mutation
    const appealMutation = useMutation({
        mutationFn: ({ recordId, text }) =>
            post(`/api/discipline/parent-records/${recordId}/appeal/`, { appeal_text: text }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['parent-discipline-records'] });
            showToast?.('Appeal submitted successfully. School staff have been notified.', 'success');
            setAppealingRecord(null);
            setAppealText('');
        },
        onError: (err) => {
            showToast?.(err.message || 'Failed to submit appeal', 'error');
        }
    });

    const handleAppealSubmit = (e) => {
        e.preventDefault();
        if (!appealText.trim()) {
            showToast?.('Please write your appeal explanation before submitting', 'error');
            return;
        }
        appealMutation.mutate({
            recordId: appealingRecord.id,
            text: appealText.trim()
        });
    };

    // Scholar Filter Options (Just real student names, no grade)
    const childOptions = useMemo(() => {
        return [
            { name: 'All Scholars', value: '' },
            ...children.map(c => {
                const fullName = c.full_name || c.name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `Scholar ${c.student_id || c.id}`;
                return {
                    name: fullName.trim(),
                    value: String(c.id)
                };
            })
        ];
    }, [children]);

    // Academic Year Options
    const yearOptions = useMemo(() => {
        const rawYears = Array.isArray(years) ? years : (years?.results || []);
        return [
            { name: 'All Academic Years', value: '' },
            ...rawYears.map(y => ({
                name: `${y.name} ${y.is_current ? '(Current Year)' : ''}`.trim(),
                value: String(y.id)
            }))
        ];
    }, [years]);

    // Academic Term Options
    const termOptions = useMemo(() => {
        const rawTerms = Array.isArray(terms) ? terms : (terms?.results || []);
        const filtered = selectedYear
            ? rawTerms.filter(t => String(t.year) === String(selectedYear))
            : rawTerms;
        return [
            { name: 'All Terms', value: '' },
            ...filtered.map(t => ({
                name: `${t.name} ${t.is_current ? '• Current' : ''}`.trim(),
                value: String(t.id)
            }))
        ];
    }, [terms, selectedYear]);

    // Split records
    const meritRecords = useMemo(() => {
        return records.filter(r => r.category_type === 'merit');
    }, [records]);

    const demeritRecords = useMemo(() => {
        return records.filter(r => r.category_type === 'demerit');
    }, [records]);

    // Apply client search & filters
    const filteredMerits = useMemo(() => {
        return meritRecords.filter(rec => {
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const matchName = rec.student_name?.toLowerCase().includes(q);
                const matchCat = rec.category_name?.toLowerCase().includes(q);
                const matchTitle = rec.title?.toLowerCase().includes(q);
                const matchDesc = rec.description?.toLowerCase().includes(q);
                const matchTeacher = rec.logged_by_name?.toLowerCase().includes(q);
                if (!matchName && !matchCat && !matchTitle && !matchDesc && !matchTeacher) return false;
            }
            return true;
        });
    }, [meritRecords, searchQuery]);

    const filteredDemerits = useMemo(() => {
        return demeritRecords.filter(rec => {
            if (filterSeverity !== 'all' && rec.severity !== filterSeverity) return false;
            if (filterStatus !== 'all' && rec.status !== filterStatus) return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const matchName = rec.student_name?.toLowerCase().includes(q);
                const matchCat = rec.category_name?.toLowerCase().includes(q);
                const matchTitle = rec.title?.toLowerCase().includes(q);
                const matchDesc = rec.description?.toLowerCase().includes(q);
                const matchRes = rec.resolution_note?.toLowerCase().includes(q);
                const matchTeacher = rec.logged_by_name?.toLowerCase().includes(q);
                if (!matchName && !matchCat && !matchTitle && !matchDesc && !matchRes && !matchTeacher) return false;
            }
            return true;
        });
    }, [demeritRecords, filterSeverity, filterStatus, searchQuery]);

    const isFiltered = (selectedChildId !== '' && children.length > 1) || selectedYear !== '' || selectedTerm !== '' || (activeTab === 'demerits' && (filterSeverity !== 'all' || filterStatus !== 'all'));

    const handleResetFilters = () => {
        setSelectedChildId('');
        onChildChange?.('');
        setSelectedYear('');
        setSelectedTerm('');
        setFilterSeverity('all');
        setFilterStatus('all');
    };

    const activeList = activeTab === 'merits' ? filteredMerits : filteredDemerits;

    return (
        <div className="space-y-6">
            {/* Modern Header matching Screenshot 2 */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2">
                <div className="space-y-2 max-w-xl">
                    <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFC425]/10 text-[#18216D] text-[10px] font-black uppercase tracking-[0.2em] rounded-full border border-[#FFC425]/20">
                            <ScaleIcon className="w-3 h-3 text-[#FFC425]" />
                            Kianda Character Portfolio
                        </div>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black text-[#18216D] uppercase tracking-tighter">
                        Character <span className="text-[#3B82F6]">Portfolio</span>
                    </h1>
                    <p className="text-slate-500 font-medium italic text-xs sm:text-sm">
                        Track positive character achievements, view commendations, and monitor disciplinary entries.
                    </p>
                </div>

                {/* Search Bar - Responsive */}
                <div className="flex items-center w-full md:w-auto">
                    <div className="relative group w-full md:w-80">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors pointer-events-none">
                            <MagnifyingGlassIcon className="w-4 h-4" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search commendations, infractions..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D] placeholder:text-slate-400"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600 p-1"
                            >
                                <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Centered Underline Tabs & Filters Button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-200 pb-1 sm:pb-0 gap-3">
                <div className="flex-1 flex justify-center overflow-x-auto custom-scrollbar">
                    <nav className="flex space-x-6 sm:space-x-12 justify-center" aria-label="Character Tabs">
                        <button
                            type="button"
                            onClick={() => setActiveTab('merits')}
                            className={`pb-3.5 px-3 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 whitespace-nowrap ${
                                activeTab === 'merits'
                                    ? 'text-[#18216D] border-[#18216D]'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                            }`}
                        >
                            <TrophyIcon className={`w-4 h-4 ${activeTab === 'merits' ? 'text-amber-500' : 'text-slate-400'}`} />
                            <span>Merits ({filteredMerits.length})</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('demerits')}
                            className={`pb-3.5 px-3 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 whitespace-nowrap ${
                                activeTab === 'demerits'
                                    ? 'text-[#18216D] border-[#18216D]'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                            }`}
                        >
                            <ExclamationTriangleIcon className={`w-4 h-4 ${activeTab === 'demerits' ? 'text-rose-500' : 'text-slate-400'}`} />
                            <span>Demerits ({filteredDemerits.length})</span>
                        </button>
                    </nav>
                </div>

                {/* Pop-Out Filters Button */}
                <div className="flex items-center gap-2.5 pb-2 justify-end">
                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 border transition-all ${
                            showFilters || isFiltered
                                ? 'bg-[#18216D] text-white border-[#18216D] shadow-sm'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        <FunnelIcon className="w-3.5 h-3.5" />
                        <span>Filters</span>
                        {isFiltered && (
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        )}
                    </button>
                </div>
            </div>

            {/* Pop-Out Filters Section */}
            {showFilters && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 animate-fadeIn">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                            <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                Filter Options ({activeTab === 'merits' ? 'Merits' : 'Demerits'})
                            </span>
                            {isFiltered && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                    Filtered
                                </span>
                            )}
                        </div>
                        {isFiltered && (
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                        {/* Scholar Filter (if parent has multiple children) */}
                        {children.length > 1 && (
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                    Scholar
                                </label>
                                <FilterDropdown
                                    value={selectedChildId}
                                    onChange={(val) => {
                                        setSelectedChildId(val || '');
                                        onChildChange?.(val || '');
                                    }}
                                    options={childOptions}
                                    placeholder="All Scholars"
                                    className="w-full"
                                />
                            </div>
                        )}

                        {/* Academic Year */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Academic Year
                            </label>
                            <FilterDropdown
                                value={selectedYear}
                                onChange={(val) => {
                                    setSelectedYear(val || '');
                                    setSelectedTerm('');
                                }}
                                options={yearOptions}
                                placeholder="All Academic Years"
                                className="w-full"
                            />
                        </div>

                        {/* Academic Term */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Academic Term
                            </label>
                            <FilterDropdown
                                value={selectedTerm}
                                onChange={(val) => setSelectedTerm(val || '')}
                                options={termOptions}
                                placeholder="All Terms"
                                className="w-full"
                            />
                        </div>

                        {/* Demerit-specific filters */}
                        {activeTab === 'demerits' && (
                            <>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Severity
                                    </label>
                                    <FilterDropdown
                                        value={filterSeverity}
                                        onChange={(val) => setFilterSeverity(val || 'all')}
                                        options={[
                                            { name: 'All Severities', value: 'all' },
                                            { name: 'Minor', value: 'minor' },
                                            { name: 'Moderate', value: 'moderate' },
                                            { name: 'Major', value: 'major' },
                                            { name: 'Severe', value: 'severe' },
                                        ]}
                                        placeholder="All Severities"
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Governance Status
                                    </label>
                                    <FilterDropdown
                                        value={filterStatus}
                                        onChange={(val) => setFilterStatus(val || 'all')}
                                        options={[
                                            { name: 'All Statuses', value: 'all' },
                                            { name: 'Logged', value: 'logged' },
                                            { name: 'Under Action', value: 'under_action' },
                                            { name: 'Resolved', value: 'resolved' },
                                            { name: 'Escalated', value: 'escalated' },
                                        ]}
                                        placeholder="All Statuses"
                                        className="w-full"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Records Content */}
            {isLoading ? (
                <div className="flex justify-center py-16">
                    <LoadingSpinner />
                </div>
            ) : activeList.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
                    <div className="w-16 h-16 rounded-2xl bg-slate-50 text-slate-300 mx-auto flex items-center justify-center text-2xl mb-3">
                        {activeTab === 'merits' ? (
                            <TrophyIcon className="w-8 h-8 text-amber-300" />
                        ) : (
                            <ExclamationTriangleIcon className="w-8 h-8 text-rose-300" />
                        )}
                    </div>
                    <p className="text-sm font-black text-[#18216D]">
                        No {activeTab === 'merits' ? 'Merit' : 'Demerit'} Records
                    </p>
                    <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                        {searchQuery || isFiltered
                            ? 'No records match your active search and filter criteria.'
                            : `Your child currently has no recorded ${activeTab === 'merits' ? 'merit' : 'demerit'} entries.`}
                    </p>
                    {(searchQuery || isFiltered) && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery('');
                                handleResetFilters();
                            }}
                            className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
                        >
                            Clear Filters & Search
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* 1. Desktop Table View (Matching Teachers Component) */}
                    <div className="hidden md:block bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/75 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <th className="py-4 px-6">Scholar</th>
                                        <th className="py-4 px-6">Category</th>
                                        {activeTab === 'demerits' && (
                                            <th className="py-4 px-6 text-center">Severity</th>
                                        )}
                                        <th className="py-4 px-6 text-center">Points</th>
                                        <th className="py-4 px-6">
                                            {activeTab === 'merits' ? 'Commendation Notes' : 'Offense Details'}
                                        </th>
                                        {activeTab === 'demerits' && (
                                            <th className="py-4 px-6">School Resolution</th>
                                        )}
                                        <th className="py-4 px-6">Logged By</th>
                                        <th className="py-4 px-6">Date</th>
                                        <th className="py-4 px-6 text-center">Status</th>
                                        {activeTab === 'demerits' && (
                                            <th className="py-4 px-6 text-center">Action</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-xs">
                                    {activeList.map(rec => (
                                        <tr
                                            key={rec.id}
                                            className={`transition-colors ${
                                                rec.category_type === 'merit'
                                                    ? 'hover:bg-emerald-50/20'
                                                    : 'hover:bg-rose-50/20'
                                            }`}
                                        >
                                            {/* Scholar info */}
                                            <td className="py-4 px-6">
                                                <div>
                                                    <p className="font-black text-[#18216D] text-xs sm:text-sm">
                                                        {rec.student_name}
                                                    </p>
                                                    {rec.student_id_number && (
                                                        <p className="text-[10px] font-bold text-slate-400">
                                                            Adm: {rec.student_id_number}
                                                        </p>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Category & Title */}
                                            <td className="py-4 px-6">
                                                <div className="space-y-0.5">
                                                    {rec.title && (
                                                        <p className="font-black text-[#18216D] text-xs">
                                                            {rec.title}
                                                        </p>
                                                    )}
                                                    <p className={rec.title ? "font-medium text-slate-600 text-xs" : "font-black text-slate-800 text-xs"}>
                                                        {rec.category_name}
                                                    </p>
                                                </div>
                                            </td>

                                            {/* Severity (Demerits only) */}
                                            {activeTab === 'demerits' && (
                                                <td className="py-4 px-6 text-center">
                                                    <span className={`inline-block px-2.5 py-0.5 rounded-md text-[10px] uppercase font-bold ${
                                                        rec.severity === 'severe' || rec.severity === 'major'
                                                            ? 'bg-rose-100 text-rose-800'
                                                            : 'bg-slate-100 text-slate-600'
                                                    }`}>
                                                        {rec.severity || 'Minor'}
                                                    </span>
                                                </td>
                                            )}

                                            {/* Points */}
                                            <td className="py-4 px-6 text-center">
                                                <span className={`inline-block px-3 py-1 rounded-xl text-xs font-black ${
                                                    rec.category_type === 'merit'
                                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                        : 'bg-rose-50 text-rose-700 border border-rose-200'
                                                }`}>
                                                    {rec.category_type === 'merit' ? `+${rec.points}` : `-${rec.points}`} pts
                                                </span>
                                            </td>

                                            {/* Notes / Description */}
                                            <td className="py-4 px-6 max-w-xs">
                                                <p className="text-slate-700 font-medium text-xs line-clamp-2">
                                                    {rec.description || '—'}
                                                </p>
                                            </td>

                                            {/* Resolution Note (Demerits only) */}
                                            {activeTab === 'demerits' && (
                                                <td className="py-4 px-6 max-w-xs">
                                                    {rec.status === 'resolved' && rec.resolution_note ? (
                                                        <div className="bg-emerald-50/80 p-2.5 rounded-lg border border-emerald-100 text-[11px] text-emerald-950">
                                                            <p className="font-bold text-emerald-800 text-[9px] uppercase tracking-wider mb-0.5">
                                                                Resolution Note
                                                            </p>
                                                            <p className="line-clamp-2">{rec.resolution_note}</p>
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs italic">None</span>
                                                    )}
                                                </td>
                                            )}

                                            {/* Logged By (Teacher) */}
                                            <td className="py-4 px-6 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5 text-xs text-slate-700 font-bold">
                                                    <UserCircleIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                                    <span>{rec.logged_by_name || 'Staff'}</span>
                                                </div>
                                            </td>

                                            {/* Date */}
                                            <td className="py-4 px-6 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5 text-xs text-slate-600 font-bold">
                                                    <CalendarIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                    <span>
                                                        {new Date(rec.logged_at).toLocaleDateString(undefined, {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="py-4 px-6 text-center whitespace-nowrap">
                                                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                    rec.status === 'resolved'
                                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                        : rec.status === 'under_action'
                                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                                                }`}>
                                                    {rec.status.replace('_', ' ')}
                                                </span>
                                            </td>

                                            {/* Action Column (Demerits only) */}
                                            {activeTab === 'demerits' && (
                                                <td className="py-4 px-6 text-center whitespace-nowrap">
                                                    {rec.can_appeal ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setAppealingRecord(rec);
                                                                setAppealText('');
                                                            }}
                                                            className="px-3.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all inline-flex items-center gap-1.5 shadow-sm active:scale-95"
                                                        >
                                                            <ScaleIcon className="w-3.5 h-3.5" />
                                                            <span>Appeal</span>
                                                        </button>
                                                    ) : rec.existing_appeal ? (
                                                        <span className="px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg text-[10px] font-bold inline-block">
                                                            Appeal Pending
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs">—</span>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 2. Mobile Card View (Enhanced & Touch-Friendly) */}
                    <div className="block md:hidden space-y-4">
                        {activeList.map(rec => (
                            <div
                                key={rec.id}
                                className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-all space-y-3.5"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="space-y-1">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <span
                                                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                    rec.category_type === 'merit'
                                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                        : 'bg-rose-50 text-rose-700 border border-rose-200'
                                                }`}
                                            >
                                                <i className={`fas ${rec.category_type === 'merit' ? 'fa-plus' : 'fa-minus'} text-[8px]`}></i>
                                                {rec.category_type}
                                            </span>
                                            <span className="text-sm font-black text-[#18216D]">{rec.category_name}</span>
                                            {rec.severity && (
                                                <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                                                    {rec.severity}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 font-bold">
                                            {rec.student_name}
                                            {rec.student_id_number && ` • Adm ${rec.student_id_number}`}
                                        </p>
                                    </div>

                                    <span
                                        className={`text-sm font-black px-2.5 py-1 rounded-xl shrink-0 ${
                                            rec.category_type === 'merit'
                                                ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                                                : 'text-rose-700 bg-rose-50 border border-rose-200'
                                        }`}
                                    >
                                        {rec.category_type === 'merit' ? `+${rec.points}` : `-${rec.points}`} pts
                                    </span>
                                </div>

                                {/* Headline Title or Notes */}
                                {rec.title && (
                                    <p className="text-xs font-bold text-slate-800">
                                        {rec.title}
                                    </p>
                                )}

                                {rec.description && (
                                    <p className="text-xs text-slate-600 leading-relaxed bg-slate-50/70 p-3 rounded-xl border border-slate-100">
                                        {rec.description}
                                    </p>
                                )}

                                {/* Resolution note (strictly visible if status = 'resolved') */}
                                {rec.status === 'resolved' && rec.resolution_note && (
                                    <div className="bg-emerald-50/80 p-3 rounded-xl border border-emerald-100">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-1">
                                            <i className="fas fa-check-circle mr-1"></i> School Resolution
                                        </p>
                                        <p className="text-xs font-medium text-emerald-950 whitespace-pre-wrap">
                                            {rec.resolution_note}
                                        </p>
                                    </div>
                                )}

                                {/* Pending appeal status */}
                                {rec.existing_appeal && (
                                    <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
                                        <i className="fas fa-clock text-amber-600 mt-0.5"></i>
                                        <div>
                                            <span className="font-black">Your Appeal is Under Review:</span>
                                            <p className="text-amber-800 mt-0.5">"{rec.existing_appeal.appeal_text}"</p>
                                        </div>
                                    </div>
                                )}

                                {/* Card Footer: Teacher, Date, Status & Action */}
                                <div className="flex flex-wrap items-center justify-between gap-2 pt-2.5 border-t border-slate-100 text-[11px]">
                                    <div className="flex flex-wrap items-center gap-3 text-slate-400 font-bold">
                                        <div className="flex items-center gap-1 text-slate-600">
                                            <UserCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                                            <span>{rec.logged_by_name || 'Staff'}</span>
                                        </div>
                                        <span>•</span>
                                        <span>
                                            {new Date(rec.logged_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                rec.status === 'resolved'
                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                    : rec.status === 'under_action'
                                                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                                            }`}
                                        >
                                            {rec.status.replace('_', ' ')}
                                        </span>

                                        {rec.can_appeal && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setAppealingRecord(rec);
                                                    setAppealText('');
                                                }}
                                                className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-colors inline-flex items-center gap-1 active:scale-95"
                                            >
                                                <ScaleIcon className="w-3 h-3" />
                                                <span>Appeal</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Appeal Modal */}
            {appealingRecord && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden">
                        <div className="bg-[#18216D] px-6 sm:px-8 py-5 text-white flex items-center justify-between">
                            <div>
                                <h3 className="text-base sm:text-lg font-black uppercase tracking-tight">
                                    Submit Discipline Appeal
                                </h3>
                                <p className="text-indigo-200 text-xs mt-0.5">
                                    Present your perspective for review by school leadership.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setAppealingRecord(null)}
                                className="text-white/70 hover:text-white p-2 rounded-lg"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleAppealSubmit} className="p-6 sm:p-8 space-y-5">
                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs">
                                <p className="font-black text-[#18216D]">{appealingRecord.category_name}</p>
                                <p className="text-slate-500 mt-0.5">
                                    Severity: {appealingRecord.severity || 'Standard'} • {appealingRecord.points} points
                                    {appealingRecord.logged_by_name ? ` • Recorded by ${appealingRecord.logged_by_name}` : ''}
                                </p>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                    Appeal Statement *
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={appealText}
                                    onChange={(e) => setAppealText(e.target.value)}
                                    placeholder="Explain any mitigating circumstances, clarifications, or reasons for reconsideration..."
                                    className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#18216D] outline-none"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">
                                    Appeals are routed directly to assigned follow-up staff and departmental leadership.
                                </p>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setAppealingRecord(null)}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={appealMutation.isPending}
                                    className="px-6 py-2.5 bg-[#18216D] hover:bg-[#121952] text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                                >
                                    Submit Appeal
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParentDisciplineTab;
